// ── Widget modules (unchanged — kept for tree-shaking and direct imports) ────
export * as DetectionVolumeWidget from "./detection-volume";
export * as DetectionPerDomainWidget from "./detection-per-domain";
export * as PirateSourceDistributionWidget from "./pirate-source-distribution";
export * as DetectionPerEngineWidget from "./detection-per-engine";
export * as ResponsePerEngineWidget from "./response-per-engine";
export * as KpiStatisticsWidget from "./kpi-statistics";
export * as MetricKpiWidget from "./metric-kpi";
export * as IpBlockingIndicatorWidget from "./ip-blocking-indicator";
export * as IpBlockingSummaryWidget from "./ip-blocking-summary";
export * as SafeBlockingSummaryWidget from "./safe-to-block";
export type { SafeBlockingSummaryConfig } from "./safe-to-block";
export * as BlockingOverviewWidget from "./blocking-overview";
export type { BlockingOverviewConfig } from "./blocking-overview";
export * as IpBlockingIspRankingWidget from "./isp-blocking-rate";
export * as PiracyAudienceIndicatorWidget from "./piracy-audience-indicator";
export * as OttPiracyIndicatorWidget from "./ott-piracy-indicator";
export * as CdnsaIndicatorWidget from "./cdnsa-indicator";
export * as CdnsaCdnComparisonWidget from "./cdnsa-cdn-comparison";
export type { CdnsaCdnComparisonConfig } from "./cdnsa-cdn-comparison";
export type { CdnsaCdnComparisonData } from "./cdnsa-cdn-comparison/types";
export * as CdnsaDailyTrendWidget from "./cdnsa-daily-trend";
export type { CdnsaDailyTrendConfig } from "./cdnsa-daily-trend";
export type { CdnsaDailyTrendData } from "./cdnsa-daily-trend/types";
export * as CdnsaAccountWaterfallWidget from "./cdnsa-account-waterfall";
export type { CdnsaAccountWaterfallConfig } from "./cdnsa-account-waterfall";
export type { CdnsaAccountWaterfallData } from "./cdnsa-account-waterfall/types";
export * as LiveKpiIndicatorWidget from "./live-kpi-indicator";
export * as IpBlockingTopHostsWidget from "./top-detected-hosts";
export * as MetricSummaryWidget from "./metric-summary";
export * as EisScoreWidget from "./eis-score";
export * as PiracyAudienceTrendWidget from "./piracy-audience-trend";
export * as BlockingPeriodSpotlightWidget from "./blocking-period-spotlight";
export * as IpBlockingTrendWidget from "./blocking-trend";
export * as StackedCategoryBarsWidget from "./stacked-category-bars";
export * as NetworkIdWeeklyWidget from "./network-id-weekly";
export * as WeightedTermCloudWidget from "./weighted-term-cloud";
export * as SupplyChainSankeyWidget from "./supply-chain-sankey";
export * as EnginesHealthWidget from "./engines-health";
export * as InfringementRecordsWidget from "./infringement-records";
export * as SystemAnomaliesWidget from "./system-anomalies";
export * as EventTimelineWidget from "./event-timeline";
export * as EventEvolutionWidget from "./event-evolution";
export * as EventScopeWidget from "./event-scope";
export * as CampaignListWidget from "./campaign-list";
export * as EventProtectionWidget from "./event-protection";
export * as EventTimelineAdvancedWidget from "./event-timeline-advanced";
export * as ProtectionOverviewEisTrendWidget from "./protection-overview-eis-trend";
export * as ProtectionOverviewCampaignsWidget from "./protection-overview-campaigns";
export * as ProtectionOverviewCampaignEventsWidget from "./protection-overview-campaign-events";
export * as GroupedMetricsTableWidget from "./grouped-metrics-table";
export * as CategoryHeatmapTableWidget from "./category-heatmap-table";
export * as FlowGeoMapWidget from "./flow-geo-map";
export * as EventRestreamingServicesWidget from "./event-restreaming-services";
export * as EventCdnThievesWidget from "./event-cdn-thieves";
export * as CdnGatekeeperResponsesWidget from "./cdn-gatekeeper-responses";
export * as RestreamingServerBlockingWidget from "./restreaming-server-blocking";
export * as SocialMediaPostsWidget from "./social-media-posts";
export * as TextNoteWidget from "./text-note";

// ── Widget definitions ────────────────────────────────────────────────────────
//
// Each widget is responsible for its own WidgetDefinition. This list is the
// single integration point: adding a widget means creating its definition file
// and appending one import here — no edits to the host application.
//
import type { WidgetDefinition } from "@nexus/widget-sdk";
import { definition as detectionVolume } from "./detection-volume/definition";
import { definition as detectionPerDomain } from "./detection-per-domain/definition";
import { definition as pirateSourceDistribution } from "./pirate-source-distribution/definition";
import { definition as detectionPerEngine } from "./detection-per-engine/definition";
import { definition as responsePerEngine } from "./response-per-engine/definition";
import { definition as kpiStatistics } from "./kpi-statistics/definition";
import { definition as metricKpi } from "./metric-kpi/definition";
import { definition as ipBlockingIndicator } from "./ip-blocking-indicator/definition";
import { definition as ipBlockingSummary } from "./ip-blocking-summary/definition";
import { definition as safeBlockingSummary } from "./safe-to-block/definition";
import { definition as blockingOverview } from "./blocking-overview/definition";
import { definition as ipBlockingIspRanking } from "./isp-blocking-rate/definition";
import { definition as piracyAudienceIndicator } from "./piracy-audience-indicator/definition";
import { definition as ottPiracyIndicator } from "./ott-piracy-indicator/definition";
import { definition as cdnsaIndicator } from "./cdnsa-indicator/definition";
import { definition as cdnsaCdnComparison } from "./cdnsa-cdn-comparison/definition";
import { definition as cdnsaDailyTrend } from "./cdnsa-daily-trend/definition";
import { definition as cdnsaAccountWaterfall } from "./cdnsa-account-waterfall/definition";
import { definition as liveKpiIndicator } from "./live-kpi-indicator/definition";
import { definition as ipBlockingTopHosts } from "./top-detected-hosts/definition";
import { definition as metricSummary } from "./metric-summary/definition";
import { definition as eisScore } from "./eis-score/definition";
import { definition as piracyAudienceTrend } from "./piracy-audience-trend/definition";
import { definition as blockingPeriodSpotlight } from "./blocking-period-spotlight/definition";
import { definition as ipBlockingTrend } from "./blocking-trend/definition";
import { definition as stackedCategoryBars } from "./stacked-category-bars/definition";
import { definition as networkIdWeekly } from "./network-id-weekly/definition";
import { definition as weightedTermCloud } from "./weighted-term-cloud/definition";
import { definition as supplyChainSankey } from "./supply-chain-sankey/definition";
import { definition as enginesHealth } from "./engines-health/definition";
import { definition as infringementRecords } from "./infringement-records/definition";
import { definition as systemAnomalies } from "./system-anomalies/definition";
import { definition as eventTimeline } from "./event-timeline/definition";
import { definition as eventTimelineAdvanced } from "./event-timeline-advanced/definition";
import { definition as eventEvolution } from "./event-evolution/definition";
import { definition as eventScope } from "./event-scope/definition";
import { definition as campaignList } from "./campaign-list/definition";
import { definition as eventProtection } from "./event-protection/definition";
import { definition as protectionOverviewEisTrend } from "./protection-overview-eis-trend/definition";
import { definition as protectionOverviewCampaigns } from "./protection-overview-campaigns/definition";
import { definition as protectionOverviewCampaignEvents } from "./protection-overview-campaign-events/definition";
import { definition as groupedMetricsTable } from "./grouped-metrics-table/definition";
import { definition as categoryHeatmapTable } from "./category-heatmap-table/definition";
import { definition as flowGeoMap } from "./flow-geo-map/definition";
import { definition as eventRestreamingServices } from "./event-restreaming-services/definition";
import { definition as eventCdnThieves } from "./event-cdn-thieves/definition";
import { definition as cdnGatekeeperResponses } from "./cdn-gatekeeper-responses/definition";
import { definition as restreamingServerBlocking } from "./restreaming-server-blocking/definition";
import { definition as socialMediaPosts } from "./social-media-posts/definition";
import { definition as textNote } from "./text-note/definition";

/**
 * All first-party widget definitions, in registration order.
 * Import this in the host registry — do not import individual definitions from here.
 */
function completeData(definition: WidgetDefinition, reason: string): WidgetDefinition {
  return {
    ...definition,
    module: {
      ...definition.module,
      getDataRequirements: () =>
        Object.fromEntries(
          Object.keys(definition.module.manifest.dataRequests).map((key) => [
            key,
            { mode: "complete" as const, reason },
          ]),
        ),
    },
  };
}

export const widgetDefinitions: readonly WidgetDefinition[] = [
  completeData(detectionVolume, "All bounded time buckets support the trend, tooltip and zoom."),
  completeData(detectionPerDomain, "All domain counts and trends support comparison and tooltips."),
  completeData(
    pirateSourceDistribution,
    "The distribution needs every category for shares and totals.",
  ),
  completeData(
    detectionPerEngine,
    "The bounded engine table needs its counts, expected counts and trends.",
  ),
  completeData(responsePerEngine, "The response table compares every engine in the bounded scope."),
  completeData(kpiStatistics, "The fixed summary displays all four KPI statistics."),
  metricKpi,
  ipBlockingIndicator,
  completeData(
    ipBlockingSummary,
    "Counts and ratios retain the full cohort or sending evidence summary.",
  ),
  completeData(
    safeBlockingSummary,
    "Safety counts and denominator evidence explain the displayed ratio.",
  ),
  blockingOverview,
  completeData(
    ipBlockingIspRanking,
    "All bounded operator rows support sorting and weighted rankings.",
  ),
  piracyAudienceIndicator,
  ottPiracyIndicator,
  cdnsaIndicator,
  cdnsaCdnComparison,
  cdnsaDailyTrend,
  cdnsaAccountWaterfall,
  liveKpiIndicator,
  metricSummary,
  eisScore,
  piracyAudienceTrend,
  blockingPeriodSpotlight,
  completeData(
    ipBlockingTrend,
    "All daily cohort or effectiveness counts retain gaps and ratio denominators.",
  ),
  ipBlockingTopHosts,
  completeData(
    stackedCategoryBars,
    "All categories determine totals and the interactive Other grouping.",
  ),
  completeData(
    networkIdWeekly,
    "Every week and group determines weekly shares, the top two and weekly maxima.",
  ),
  completeData(weightedTermCloud, "All bounded term weights determine relative size and rank."),
  completeData(supplyChainSankey, "The flow needs all linked nodes and weighted edges."),
  completeData(enginesHealth, "The fixed status table displays every engine in scope."),
  completeData(
    infringementRecords,
    "Each bounded cursor page supplies table columns and detail drill-down.",
  ),
  completeData(
    systemAnomalies,
    "Each bounded anomaly page supplies statuses, detail context and pagination.",
  ),
  completeData(eventTimeline, "All events in the time window remain selectable scope targets."),
  completeData(
    eventTimelineAdvanced,
    "The monitoring timeline needs all scoped events and status context.",
  ),
  completeData(
    eventEvolution,
    "The scoped evolution retains selectable series, markers and configured breakdowns.",
  ),
  completeData(
    eventScope,
    "The bounded event list provides every selectable event and its identity.",
  ),
  completeData(campaignList, "The schedule retains all campaign groups and drill-down identities."),
  completeData(
    eventProtection,
    "The protection matrix needs schedule memberships and live event context.",
  ),
  completeData(
    protectionOverviewEisTrend,
    "All edition trend points and tenant metadata are displayed.",
  ),
  completeData(
    protectionOverviewCampaigns,
    "The edition campaign list retains ranking and drill-down context.",
  ),
  completeData(
    protectionOverviewCampaignEvents,
    "All campaign event rows retain their status, identity and drill-down.",
  ),
  completeData(
    groupedMetricsTable,
    "The grouped comparison table needs each row's metrics and scope context.",
  ),
  completeData(
    categoryHeatmapTable,
    "Every bounded cell contributes to the matrix and its color scale.",
  ),
  completeData(
    flowGeoMap,
    "The bounded geography flow needs all nodes, edges and tooltip context.",
  ),
  completeData(
    eventRestreamingServices,
    "The service ranking retains counts and detail drill-down evidence.",
  ),
  completeData(
    eventCdnThieves,
    "The CDN ranking retains all scoped rows and their evidence context.",
  ),
  completeData(
    cdnGatekeeperResponses,
    "The response table displays each bounded status and timing column.",
  ),
  completeData(
    restreamingServerBlocking,
    "The server table retains blocking history and operator details.",
  ),
  completeData(
    socialMediaPosts,
    "All bounded platform groups and posts remain available for interactive browsing.",
  ),
  textNote,
];

// ── Shared exports ────────────────────────────────────────────────────────────
// Kept for the host. Pieces shared between widgets live in @nexus/widget-kit.
export {
  buildMatrixEvents,
  buildTenantGroups,
  CampaignLiveDot as LiveDot,
  EventTypeIcon,
  formatDetectionDomain,
  isEventLive,
} from "@nexus/widget-kit";
export type { CampaignMatrixEvent, CampaignTenantGroup } from "@nexus/widget-kit";

export type {
  DetectionVolumeConfig,
  DetectionVolumeData,
  DetectionVolumePoint,
} from "./detection-volume/types";

export type {
  DetectionPerDomainConfig,
  DetectionPerDomainData,
  DetectionPerDomainEntry,
} from "./detection-per-domain/types";

export type {
  PirateSourceDistributionConfig,
  PirateSourceDistributionData,
  PirateSourceDistributionEntry,
  PirateSourceType,
} from "./pirate-source-distribution/types";

export type {
  DetectionPerEngineConfig,
  DetectionPerEngineData,
  DetectionPerEngineEntry,
} from "./detection-per-engine/types";

export type {
  ResponsePerEngineConfig,
  ResponsePerEngineData,
  ResponsePerEngineEntry,
} from "./response-per-engine/types";

export { REPORT_METRICS_CONTRACT } from "@nexus/widget-kit";
export type {
  ReportFreshness,
  ReportMetric,
  ReportMetricFormat,
  ReportMetricsData,
  ReportMetricsSubject,
  ReportPeriod,
} from "@nexus/widget-kit";
export type { MetricKpiConfig, TrendPolarity } from "./metric-kpi";
export type { MetricSummaryConfig, MetricSummaryEntry } from "./metric-summary";
export type { EisScoreConfig } from "./eis-score";
export { EisBadge, eisColor } from "@nexus/widget-kit";
export { PIRACY_AUDIENCE_SERIES_CONTRACT } from "@nexus/widget-kit";
export type {
  BlockingInterval,
  BlockingIntervalStatus,
  BlockingPeriod,
  PiracyAudienceFreshness,
  PiracyAudiencePoint,
  PiracyAudienceSeriesData,
} from "@nexus/widget-kit";
export type { PiracyAudienceTrendConfig } from "./piracy-audience-trend";
export type { BlockingPeriodSpotlightConfig } from "./blocking-period-spotlight";
export { IP_BLOCKING_SERIES_CONTRACT } from "./blocking-trend/types";
export type {
  IpBlockingFreshness,
  IpBlockingPoint,
  IpBlockingSeriesData,
} from "./blocking-trend/types";
export type { IpBlockingTrendConfig } from "./blocking-trend";
export { RANKED_SHARES_CONTRACT } from "./top-detected-hosts";
export type {
  IpBlockingTopHostsConfig,
  RankedShareItem,
  RankedShareList,
  RankedSharesData,
} from "./top-detected-hosts";
export { CATEGORY_SERIES_CONTRACT } from "./stacked-category-bars/types";
export { NETWORK_ID_WEEKLY_CONTRACT } from "./network-id-weekly/types";
export type {
  NetworkIdGroupCount,
  NetworkIdWeek,
  NetworkIdWeeklyData,
} from "./network-id-weekly/types";
export type { NetworkIdWeeklyConfig } from "./network-id-weekly";
export type {
  CategoryFreshness,
  CategoryPoint,
  CategorySeriesData,
} from "./stacked-category-bars/types";
export type { StackedCategoryBarsConfig } from "./stacked-category-bars";
export { WEIGHTED_TERMS_CONTRACT } from "./weighted-term-cloud/types";
export type {
  WeightedTerm,
  WeightedTermsData,
  WeightedTermsFreshness,
} from "./weighted-term-cloud/types";
export type { WeightedTermCloudConfig } from "./weighted-term-cloud";
export { SUPPLY_CHAIN_CONTRACT, SUPPLY_CHAIN_STAGES } from "./supply-chain-sankey/types";
export type {
  SupplyChainData,
  SupplyChainFreshness,
  SupplyChainLink,
  SupplyChainNode,
  SupplyChainStage,
} from "./supply-chain-sankey/types";
export type { SupplyChainSankeyConfig } from "./supply-chain-sankey";

export type {
  KpiStatisticItem,
  KpiStatisticsConfig,
  KpiStatisticsData,
  KpiTone,
} from "./kpi-statistics/types";

export type {
  EngineHealthTenantEntry,
  EngineHealthEntry,
  EnginesHealthConfig,
  EnginesHealthData,
} from "./engines-health/types";

export type {
  InfringementRecordEntry,
  InfringementRecordsConfig,
  InfringementResponseEntry,
  InfringementRecordsData,
} from "./infringement-records/types";

export type {
  AnomalyEntry,
  AnomalyJournalEntry,
  SystemAnomaliesConfig,
  SystemAnomaliesData,
} from "./system-anomalies/types";

export type {
  EventTimelineConfig,
  EventTimelineData,
  TimelineEvent,
  TimelineProtectedEvent,
} from "./event-timeline/types";

export type {
  EventEvolutionConfig,
  EventEvolutionData,
  EventEvolutionPoint,
} from "./event-evolution/types";

export type { EventScopeConfig } from "./event-scope";

export type {
  CampaignEngineType,
  CampaignScheduleData,
  CampaignScheduleRow,
  CampaignStatus,
} from "@nexus/widget-kit";
export type { CampaignListConfig } from "./campaign-list/types";
export type { EventProtectionConfig } from "./event-protection/types";

export type { LiveMonitoringData, MonitoredEvent } from "./event-timeline-advanced/model";

export { PROTECTION_OVERVIEW_EIS_CONTRACT } from "./protection-overview-eis-trend/types";
export type {
  ProtectionOverviewEisData,
  ProtectionOverviewEisFreshness,
  ProtectionOverviewEisTenant,
} from "./protection-overview-eis-trend/types";
export type { ProtectionOverviewEisTrendConfig } from "./protection-overview-eis-trend";
export { PROTECTION_OVERVIEW_CAMPAIGNS_CONTRACT } from "./protection-overview-campaigns/types";
export type {
  ProtectionOverviewCampaignEntry,
  ProtectionOverviewCampaignTenant,
  ProtectionOverviewCampaignsData,
  ProtectionOverviewCampaignsFreshness,
} from "./protection-overview-campaigns/types";
export type { ProtectionOverviewCampaignsConfig } from "./protection-overview-campaigns";
export { PROTECTION_OVERVIEW_CAMPAIGN_EVENTS_CONTRACT } from "./protection-overview-campaign-events/types";
export type {
  ProtectionOverviewCampaignEvent,
  ProtectionOverviewCampaignEventsData,
  ProtectionOverviewCampaignEventsFreshness,
  ProtectionOverviewCampaignHeader,
} from "./protection-overview-campaign-events/types";
export type { ProtectionOverviewCampaignEventsConfig } from "./protection-overview-campaign-events";
export { GROUPED_METRICS_TABLE_CONTRACT } from "./grouped-metrics-table/types";
export type {
  GroupedMetricsTableData,
  GroupedMetricsTableFreshness,
  MetricColumn,
  MetricColumnGroup,
  MetricSubRowGroup,
  MetricTableRow,
  MetricValueKind,
} from "./grouped-metrics-table/types";
export type { GroupedMetricsTableConfig } from "./grouped-metrics-table";
export { CATEGORY_HEATMAP_TABLE_CONTRACT } from "./category-heatmap-table/types";
export type {
  CategoryHeatmapTableData,
  HeatmapColumn,
  HeatmapColumnGroup,
  HeatmapItem,
  HeatmapRow,
} from "./category-heatmap-table/types";
export type { CategoryHeatmapTableConfig } from "./category-heatmap-table";
export { FLOW_GEO_MAP_CONTRACT } from "./flow-geo-map/types";
export type {
  FlowGeoMapData,
  FlowGeoMapFreshness,
  FlowLink,
  FlowNode,
  FlowNodeKind,
  GeoAnchor,
  GeoMarker,
} from "./flow-geo-map/types";
export type { FlowGeoMapConfig } from "./flow-geo-map";

export { EVENT_RESTREAMING_SERVICES_CONTRACT } from "./event-restreaming-services/types";
export type {
  EventRestreamingService,
  EventRestreamingServicesData,
  EventRestreamingServicesFreshness,
  EventRestreamingServicesHeader,
} from "./event-restreaming-services/types";
export type { EventRestreamingServicesConfig } from "./event-restreaming-services";

export { EVENT_CDN_THIEVES_CONTRACT } from "./event-cdn-thieves/types";
export type {
  EventCdnThief,
  EventCdnThievesData,
  EventCdnThievesFreshness,
} from "./event-cdn-thieves/types";
export type { EventCdnThievesConfig } from "./event-cdn-thieves";

export { CDN_GATEKEEPER_RESPONSES_CONTRACT } from "./cdn-gatekeeper-responses/types";
export type {
  CdnGatekeeperResponsesData,
  CdnGatekeeperService,
} from "./cdn-gatekeeper-responses/types";
export type { CdnGatekeeperResponsesConfig } from "./cdn-gatekeeper-responses";

export { RESTREAMING_SERVER_BLOCKING_CONTRACT } from "./restreaming-server-blocking/types";
export type {
  RestreamingServerBlockingData,
  RestreamingServerBlockingIsp,
} from "./restreaming-server-blocking/types";
export type { RestreamingServerBlockingConfig } from "./restreaming-server-blocking";

export { SOCIAL_MEDIA_POSTS_CONTRACT } from "./social-media-posts/types";
export type {
  SocialMediaMessage,
  SocialMediaPlatform,
  SocialMediaPlatformId,
  SocialMediaPostsData,
} from "./social-media-posts/types";
export type { SocialMediaPostsConfig } from "./social-media-posts";
